import { FC, memo, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	InputAdornment,
	Skeleton,
	Stack,
	TextField,
	Typography,
	alpha,
} from "@mui/material";
import dayjs from "dayjs";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { SATOSLink } from "@/components/Application/SATOSLink";
import { Translate } from "@/components/Application/Translate";

import { UppdatedProcedureVersion } from "@/model/Procedure/UppdatedProcedureVersion";
import { UserContext } from "@/model/User/UserContext";

import { useGetUpdatedProcedures } from "@/service/ProcedureQueries";

/* ---------- Small helpers (inline) ---------- */

const LeftAccent: FC<{ color?: string }> = ({ color = "#E7EAEE" }) => (
	<Box
		sx={{
			width: 4,
			minWidth: 4,
			alignSelf: "stretch",
			borderRadius: 1,
			bgcolor: color,
			mr: 1.5,
		}}
	/>
);

const InfoChip: FC<{ label: React.ReactNode }> = ({ label }) => (
	<Chip
		size="small"
		variant="outlined"
		label={label}
		sx={{ borderRadius: 1.5 }}
	/>
);

/* ---------- Item (row-like card) ---------- */

interface iProcedureItem {
	procedure: UppdatedProcedureVersion;
}

const ProcedureItem: FC<iProcedureItem> = memo(({ procedure }) => {
	const navigate = useNavigate();
	const goToCompare = () =>
		navigate(
			`/viewuppdatednode/${procedure.ProcedureId}/parrent/${procedure.NodeSchemeId}`,
		);

	return (
		<Grid item xs={12} sm={6} lg={4}>
			<Card
				variant="outlined"
				sx={(t) => ({
					height: "100%",
					borderRadius: 3,
					borderColor: alpha(t.palette.divider, 0.6),
					transition:
						"transform .18s ease, box-shadow .18s ease, border-color .18s ease",
					"&:hover": {
						transform: "translateY(-2px)",
						boxShadow: "0 10px 30px rgba(0,0,0,.08)",
						borderColor: alpha(t.palette.primary.main, 0.25),
					},
				})}
			>
				<CardActionArea onClick={goToCompare} sx={{ height: "100%" }}>
					<CardContent sx={{ p: 2.25 }}>
						<Stack direction="row" spacing={1.5} alignItems="flex-start">
							<LeftAccent color="#F5D565" />
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									variant="h6"
									component="h2"
									sx={{ fontWeight: 700 }}
									noWrap
								>
									<SATOSLink
										to="AccessProcedure"
										params={[procedure.ProcedureId]}
									>
										{procedure.ProcedureName}
									</SATOSLink>
								</Typography>

								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									flexWrap="wrap"
									useFlexGap
									sx={{ mt: 0.5 }}
								>
									<InfoChip
										label={
											<Typography variant="caption">
												<Translate String="Procedures.UpdateList.Version">
													Version:
												</Translate>{" "}
												{procedure.NewRevision}
											</Typography>
										}
									/>
									<InfoChip
										label={
											<Typography variant="caption" noWrap>
												<Translate String="Procedures.UpdateList.Node">
													Node:
												</Translate>{" "}
												{procedure.NodeName}
											</Typography>
										}
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ ml: "auto" }}
										title={dayjs(procedure.Updated).format(
											"dddd, DD MMM YYYY • HH:mm",
										)}
									>
										<Translate String="Procedures.UpdateList.Updated">
											Updated:
										</Translate>{" "}
										{dayjs(procedure.Updated).format("DD/MM/YY")}
									</Typography>
								</Stack>
							</Box>

							<IconButton size="small" sx={{ ml: 0.5 }}>
								<ChevronRightIcon fontSize="small" />
							</IconButton>
						</Stack>

						<Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
							<Button
								variant="outlined"
								color="info"
								size="small"
								startIcon={<PreviewIcon />}
								component={Link}
								to={`/viewuppdatednode/${procedure.ProcedureId}/parrent/${procedure.NodeSchemeId}`}
								sx={{ textTransform: "none", borderRadius: 2 }}
								onClick={(e) => e.stopPropagation()}
							>
								<Translate String="Procedures.UpdateList.Review.Changes">
									Review changes
								</Translate>
							</Button>
						</Stack>
					</CardContent>
				</CardActionArea>
			</Card>
		</Grid>
	);
});
ProcedureItem.displayName = "ProcedureItem";

/* ---------- Page ---------- */

export const ShowUpdatedProcedures: FC = () => {
	const user = useContext(UserContext);
	const [companyId, setCompanyId] = useState<number>();
	const { data, isLoading } = useGetUpdatedProcedures(companyId);
	const [searchQuery, setSearchQuery] = useState("");

	useEffect(() => {
		if (user?.CompanyId) setCompanyId(user.CompanyId);
	}, [user]);

	const filtered = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		const list = data ?? [];
		return q
			? list.filter((n) => n.ProcedureName.toLowerCase().includes(q))
			: list;
	}, [data, searchQuery]);

	return (
		<AdminProtected>
			<Box sx={{ p: { xs: 2, md: 4 }, mx: "auto", mt: 2 }}>
				<Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
					<Translate String="Procedures.UpdateList.Header">
						Procedures with updated nodes
					</Translate>
				</Typography>

				{/* Search */}
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							label={
								<Translate String="Procedures.UpdateList.Search">
									Search Procedures
								</Translate>
							}
							variant="outlined"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
								endAdornment: searchQuery ? (
									<InputAdornment position="end">
										<IconButton
											size="small"
											aria-label="Clear search"
											onClick={() => setSearchQuery("")}
											edge="end"
										>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
							sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
						/>
					</Grid>
				</Grid>

				{/* List */}
				<Grid container spacing={3}>
					{isLoading ? (
						Array.from({ length: 6 }).map((_, i) => (
							<Grid item xs={12} sm={6} lg={4} key={`skeleton-${i}`}>
								<Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
									<Skeleton
										variant="text"
										height={28}
										width="80%"
										sx={{ mb: 1 }}
									/>
									<Skeleton variant="text" height={18} width="60%" />
									<Skeleton
										variant="rounded"
										height={36}
										width="40%"
										sx={{ mt: 2 }}
									/>
								</Card>
							</Grid>
						))
					) : filtered.length > 0 ? (
						filtered.map((node) => (
							<ProcedureItem
								key={`${node.ProcedureId}-${node.NodeSchemeId}-${node.NewRevision}`}
								procedure={node}
							/>
						))
					) : (
						<Grid item xs={12} textAlign="center">
							<Box
								sx={(t) => ({
									borderRadius: 3,
									p: 6,
									border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
								})}
							>
								<Typography
									variant="h6"
									component="h3"
									color="text.secondary"
									sx={{ mb: 1 }}
								>
									<Translate String="Procedures.UpdateList.Empty">
										No updated nodes
									</Translate>
								</Typography>
								<Typography variant="body2" color="text.secondary">
									<Translate String="Procedures.UpdateList.EmptyHint">
										Try adjusting your search or check back later.
									</Translate>
								</Typography>
							</Box>
						</Grid>
					)}
				</Grid>
			</Box>
		</AdminProtected>
	);
};
